import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { companyInfo } from '../data/mock';
import { useLanguage } from '../contexts/LanguageContext';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { language, t, setLang } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: t.nav.home, path: '/' },
    { name: t.nav.about, path: '/sobre' },
    { name: t.nav.services, path: '/servicos' },
    { name: t.nav.gallery, path: '/galeria' },
    { name: t.nav.testimonials, path: '/depoimentos' },
    { name: t.nav.contact, path: '/contato' }
  ];

  const handleWhatsAppClick = () => {
    window.open(`https://wa.me/${companyInfo.whatsapp}`, '_blank');
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <img 
              src="/logo.png" 
              alt="JC FLOORS & TILES" 
              className="h-16 w-auto transform group-hover:scale-105 transition-transform"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors hover:text-amber-600 ${
                  location.pathname === link.path
                    ? 'text-amber-600'
                    : 'text-gray-700'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden md:flex items-center space-x-3">
            {/* Language Selector */}
            <button
              onClick={() => setLang(language === 'pt' ? 'en' : 'pt')}
              className="flex items-center space-x-1 px-3 py-2 rounded-lg border-2 border-gray-300 hover:border-amber-600 transition-colors"
              title={language === 'pt' ? 'Switch to English' : 'Mudar para Português'}
            >
              <Globe className="w-4 h-4 text-gray-700" />
              <span className="text-sm font-medium text-gray-700 uppercase">{language}</span>
            </button>
            
            <Button
              onClick={handleWhatsAppClick}
              className="bg-green-600 hover:bg-green-700 text-white transition-colors"
            >
              <Phone className="w-4 h-4 mr-2" />
              {t.nav.whatsapp}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-gray-700 hover:text-amber-600 transition-colors"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200">
            <nav className="flex flex-col space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`text-sm font-medium transition-colors hover:text-amber-600 ${
                    location.pathname === link.path
                      ? 'text-amber-600'
                      : 'text-gray-700'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              
              {/* Language Selector Mobile */}
              <button
                onClick={() => setLang(language === 'pt' ? 'en' : 'pt')}
                className="flex items-center justify-center space-x-2 px-4 py-2 rounded-lg border-2 border-gray-300 hover:border-amber-600 transition-colors"
              >
                <Globe className="w-4 h-4 text-gray-700" />
                <span className="text-sm font-medium text-gray-700">
                  {language === 'pt' ? 'English' : 'Português'}
                </span>
              </button>
              
              <Button
                onClick={handleWhatsAppClick}
                className="bg-green-600 hover:bg-green-700 text-white w-full transition-colors"
              >
                <Phone className="w-4 h-4 mr-2" />
                {t.nav.whatsapp}
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};